/**
 * PRESENTATION of the staleness gate's server facts (2026-09-15, commit 5).
 *
 * NOTHING HERE DECIDES STALENESS. Whether a reference is stale, which reasons
 * count as stale for the advisory, and which field a reference belongs to are
 * all decided server-side (backend/routes/_master_reference_staleness.py) and
 * arrive as data: GET /procurement/{id}'s `master_reference_advisory`, and the
 * `advisory` / `stale_references` carried on a MASTER_REFERENCE_STALE or
 * RISK_CATEGORY_ATTESTATION_REQUIRED refusal. A JS copy of any of those rules
 * would be a new unregistered mirror pair. This file only turns the server's
 * references into sentences and reads fields off them.
 */

export const ADVISORY_STALE = 'stale';

export const HEADER_CATEGORY_FIELD = 'parsed_requirement.category';

export const RISK_CATEGORY_ATTESTATION_REQUIRED = 'RISK_CATEGORY_ATTESTATION_REQUIRED';

// ONE EVALUATION, EVERY MASTER (Ashok ruling, 2026-09-16). Every reference the
// server sends names its `master`. A reference WITHOUT one predates that and
// can only be a category - the server evaluated nothing else before then.
export const MASTER_CATEGORY = 'category_master';
export const MASTER_LOCATION = 'location_master';
const masterOf = (reference) => (reference && reference.master) || MASTER_CATEGORY;

// The owning step ids the server sends, as the wizard names its steps.
const STEP_LABELS = { intake: 'the Intake step', answers: 'the Answers step', items: 'the Lines step' };

// Per-master wording. The category words are unchanged.
const MASTER_WORDS = {
  [MASTER_CATEGORY]: { noun: 'spend category', plural: 'categories', kind: 'category', codeShape: 'category code' },
  [MASTER_LOCATION]: { noun: 'ship-to location', plural: 'ship-to locations', kind: 'ship-to location', codeShape: 'location code' },
};
const wordsFor = (master) => MASTER_WORDS[master] || MASTER_WORDS[MASTER_CATEGORY];

/**
 * "CODE - Name" (ruling 6), matching b3f5a00's CodeNameLabel for ship-to, or
 * the code alone when the server named nothing for it (categories, or a code
 * no master row carries).
 */
export const referenceLabel = ({ code, name }) => (code && name ? `${code} - ${name}` : (code || ''));

export const isAdvisoryStale = (advisory) => Boolean(
  advisory && advisory.status === ADVISORY_STALE
  && Array.isArray(advisory.stale_references) && advisory.stale_references.length > 0
);

/**
 * GROUPED BY CATEGORY CODE (Ashok ruling, 2026-09-15). PROC-000412 carries
 * one retired code on its header and on 50 of its 51 lines, and one line per
 * reference rendered 51 identical sentences. "Evaluate everything, report
 * everything" is an invariant of the EVALUATION - the server still lists
 * every reference, and the 422 is unchanged - not of the presentation.
 *
 * One group per DISTINCT code, in the order the server first names it (header
 * first, then lines). Two different codes are always two groups: they are two
 * different problems. The header and the lines stay distinct INSIDE a group,
 * because they are different remedies (Intake vs Lines). A reference with no
 * code (a refusal for an absent category) groups under null.
 *
 * Returns [{ code, reason, header: bool, lineNumbers: [..] }]. Nothing here
 * decides which references are stale - it only arranges the server's list.
 */
export const groupStaleReferencesByCode = (references) => {
  // GROUPED BY MASTER, THEN BY CODE (2026-09-16). The key is the pair: a
  // category code and a location code are always two problems, even if they
  // ever spelled the same. Order is the server's first mention, and the
  // server's order is its registry's - categories, then locations - so the
  // combined message reads grouped by master type without re-sorting here.
  //
  // THE HEADER IS WHATEVER HAS NO LINE NUMBER. The server sends line_number
  // null for a header reference and a number for a line; that is the
  // master-agnostic signal. Matching the category header's field name here
  // would file a ship-to header as a line with no number.
  const groups = [];
  const byKey = new Map();
  (Array.isArray(references) ? references : []).forEach((reference) => {
    if (!reference || typeof reference !== 'object') return;
    const code = reference.code ?? null;
    const master = masterOf(reference);
    const key = `${master}\u0000${code}`;
    let group = byKey.get(key);
    if (!group) {
      group = {
        master, code, name: reference.name ?? null, reason: reference.reason, header: false, lineNumbers: [],
      };
      byKey.set(key, group);
      groups.push(group);
    }
    if (reference.line_number === null || reference.line_number === undefined) {
      group.header = true;
    } else {
      group.lineNumbers.push(reference.line_number);
    }
  });
  return groups;
};

/** "1-50", "2, 7, 19", "1-3, 9". Numbers kept in the server's order. */
export const formatLineNumbers = (lineNumbers) => {
  const nums = (lineNumbers || []).filter((n) => Number.isInteger(n));
  const parts = [];
  let start = null;
  let prev = null;
  nums.forEach((n) => {
    if (start === null) { start = n; prev = n; return; }
    if (n === prev + 1) { prev = n; return; }
    parts.push(start === prev ? `${start}` : `${start}-${prev}`);
    start = n; prev = n;
  });
  if (start !== null) parts.push(start === prev ? `${start}` : `${start}-${prev}`);
  return parts.join(', ');
};

/**
 * THE BANNER'S INLINE LINE LIST (2026-09-15). Rendered beneath each code's
 * sentence, always visible - the disclosure toggle that used to hide it was
 * removed (Ashok ruling, 2026-09-15): at or below the threshold the list is one
 * row that fits by construction, above it the ranges are shorter still, so a
 * control hiding it was usually pointless. Compression earns its place only
 * at scale: "1, 2, 3, 4, 5, 6, 7, 8, 9" carries the same fact as
 * "1-9" and reads as a list. So up to PLAIN_LINE_LIST_MAX numbers are
 * listed plainly; above it they are compressed into ranges by
 * formatLineNumbers - which is what keeps 50 lines at "Affected lines: 1-50".
 *
 * WHY 10 - MEASURED, NOT CHOSEN FOR ROUNDNESS. A plain list stays readable
 * while it fits on one row; once it wraps it becomes a block of numbers the
 * buyer has to count. Measured in headless Edge with the app's body font
 * (IBM Plex Sans, text-xs 12px/16px) at the narrowest surface - the wizard
 * banner, whose line column is the viewport minus 100px of padding, border
 * and icon - on a 360px phone (236px column), with two-digit line numbers
 * (the worst common case; the BOQ row cap is 50): 10 numbers fit on one row,
 * 11 wrap. For reference: 8 at 320px, 11 at 390px, 13 for single-digit lines
 * at 360px, and the detail page's column is 18px wider than the wizard's.
 *
 * Non-consecutive numbers are never merged either way: formatLineNumbers
 * only joins consecutive runs, so 1, 3, 5 stays "1, 3, 5".
 *
 * THE TOAST IS NOT CHANGED: it still compresses at every size
 * (describeStaleReferencesForToast), as already ruled.
 */
export const PLAIN_LINE_LIST_MAX = 10;

/**
 * THE LINE LIST'S LEAD-IN, ONE WORDING FOR BOTH SURFACES (Ashok ruling,
 * 2026-09-15). "Affected lines: 1, 2, 3" reads as a fact about those lines;
 * "Lines 1, 2, 3" read as a heading. The banner and the refusal toast both
 * build their list through this, so the two cannot come to disagree about the
 * same content. Singular for one line ("Affected line: 7") - pinned by
 * StaleSurfacesLayout.test.jsx.
 */
export const formatAffectedLines = (count, listText) => (
  `${count === 1 ? 'Affected line' : 'Affected lines'}: ${listText}`
);

export const formatBannerLineNumbers = (lineNumbers) => {
  const nums = (lineNumbers || []).filter((n) => Number.isInteger(n));
  return nums.length <= PLAIN_LINE_LIST_MAX ? nums.join(', ') : formatLineNumbers(nums);
};

const linesPhrase = (count, totalLines) => {
  const noun = count === 1 ? 'line' : 'lines';
  return Number.isInteger(totalLines) && totalLines >= count
    ? `${count} of ${totalLines} ${totalLines === 1 ? 'line' : 'lines'}`
    : `${count} ${noun}`;
};

/**
 * The block's sentence. `totalLines` is the document's final line count when
 * the surface knows it; without it the count is given bare ("50 lines"),
 * never guessed.
 *
 *   Spend category 601090 is no longer active. Used on the Intake step and on
 *   50 of 51 lines - reselect it on the Intake step and update the lines on
 *   the Lines step.
 */
export const describeStaleGroup = (group, totalLines = null) => {
  if ((group.master || MASTER_CATEGORY) === MASTER_LOCATION) {
    return describeStaleLocationGroup(group, totalLines);
  }
  const count = group.lineNumbers.length;
  const where = [];
  if (group.header) where.push('the Intake step');
  if (count) where.push(linesPhrase(count, totalLines));
  const used = where.length === 2 ? `on ${where[0]} and on ${where[1]}` : `on ${where[0]}`;

  let remedy;
  if (group.header && count) {
    remedy = group.code
      ? 'update it on the Intake step and update the lines on the Lines step'
      : 'select one on the Intake step and on the Lines step';
  } else if (group.header) {
    remedy = group.code ? 'update it on the Intake step' : 'select one on the Intake step';
  } else {
    remedy = group.code
      ? (count === 1 ? 'update it on the Lines step' : 'update them on the Lines step')
      : 'select one on the Lines step';
  }

  if (!group.code) {
    return `No spend category is set ${used} - ${remedy}.`;
  }
  const status = group.reason === 'shape' ? 'is not a valid category code' : 'is no longer active';
  return `Spend category ${group.code} ${status}. Used ${used} - ${remedy}.`;
};

/**
 * A SHIP-TO LOCATION block (2026-09-16). The header ship-to is ONE value owned
 * by the Answers step, not a step the buyer "uses it on", so the header half
 * reads "Used as the ship-to location"; lines are owned by Lines.
 *
 *   Ship-to location NOI001 - NoidaOne is no longer active. Used as the
 *   ship-to location and on 3 of 5 lines - reselect it on the Answers step and
 *   update the lines on the Lines step.
 *
 * An absent ship-to is never stale (absence is allowed on both rows), so there
 * is no "no ship-to location is set" arm.
 */
const describeStaleLocationGroup = (group, totalLines) => {
  const count = group.lineNumbers.length;
  const label = referenceLabel(group);
  const where = [];
  if (group.header) where.push('as the ship-to location');
  if (count) where.push(`on ${linesPhrase(count, totalLines)}`);
  const used = where.join(' and ');
  let remedy;
  if (group.header && count) {
    remedy = 'update it on the Answers step and update the lines on the Lines step';
  } else if (group.header) {
    remedy = 'update it on the Answers step';
  } else {
    remedy = count === 1 ? 'update it on the Lines step' : 'update them on the Lines step';
  }
  const status = group.reason === 'shape' ? 'is not a valid location code' : 'is no longer active';
  return `Ship-to location ${label} ${status}. Used ${used} - ${remedy}.`;
};

/**
 * THE BANNER'S TITLE AND LEAD (2026-09-16). Unchanged wording when every group
 * is a category; a ship-to location, or a mix, is named for what it is rather
 * than being called a category.
 */
export const describeAdvisoryTitle = (groups) => {
  const list = Array.isArray(groups) ? groups : [];
  const masters = [...new Set(list.map((g) => g.master || MASTER_CATEGORY))];
  const one = list.length === 1;
  if (masters.length === 1) {
    const words = wordsFor(masters[0]);
    return one
      ? `A ${words.kind} on this requirement is no longer active`
      : `Some ${words.plural} on this requirement are no longer active`;
  }
  return 'Some master values on this requirement are no longer active';
};

/**
 * The risk-stage fork's description, naming WHICHEVER references are stale
 * (ruling 3) - never "the category" for a location:
 *   "The spend category 601090 and the ship-to location NOI001 - NoidaOne used
 *    to generate this RFP are no longer active in the procurement master."
 * Returns the kinds too, for the dialog's issued / not-issued sentences.
 */
export const describeForkReferences = (references) => {
  const groups = groupStaleReferencesByCode(references).filter((g) => g.code);
  const named = groups.map((g) => `the ${wordsFor(g.master).noun} ${referenceLabel(g)}`);
  let subject;
  if (named.length <= 1) subject = named[0] || 'the category';
  else subject = `${named.slice(0, -1).join(', ')} and ${named[named.length - 1]}`;
  const verb = named.length > 1 ? 'are' : 'is';
  const kinds = [...new Set(groups.map((g) => wordsFor(g.master).kind))];
  const kindText = kinds.length ? kinds.join(' and ') : 'category';
  return {
    sentence: `${subject.charAt(0).toUpperCase()}${subject.slice(1)} used to generate this RFP ${verb} no longer active in the procurement master.`,
    kinds: kindText,
  };
};

/**
 * Where the fork's `regenerate` arm sends the buyer: the step that owns the
 * first retired reference that needs a remedy. Intake for a category header,
 * Answers for a ship-to header, Lines otherwise.
 */
export const regenerateStepFor = (references) => {
  const list = Array.isArray(references) ? references : [];
  if (referencesIncludeHeader(list)) return 'intake';
  if (list.some((r) => r && masterOf(r) === MASTER_LOCATION && (r.line_number === null || r.line_number === undefined))) {
    return 'answers';
  }
  return 'items';
};

/**
 * The refusal toast's text: one sentence per group. A toast cannot hold a
 * disclosure, so the line numbers ride inline - compressed into ranges and
 * capped, so 50 contiguous lines read "Affected lines: 1-50." rather than 50 entries.
 */
const TOAST_LINE_SEGMENTS = 8;
export const describeStaleReferencesForToast = (references, totalLines = null) => (
  groupStaleReferencesByCode(references).map((group) => {
    const sentence = describeStaleGroup(group, totalLines);
    if (!group.lineNumbers.length) return sentence;
    const segments = formatLineNumbers(group.lineNumbers).split(', ');
    const shown = segments.slice(0, TOAST_LINE_SEGMENTS).join(', ');
    const more = segments.length > TOAST_LINE_SEGMENTS ? ` and ${segments.length - TOAST_LINE_SEGMENTS} more` : '';
    // A sentence of its own after the grouped sentence, not a parenthetical:
    // "(Affected lines: 1-50.)" would put a label inside brackets. Was
    // "(Lines 1-50.)" until 2026-09-15.
    return `${sentence} ${formatAffectedLines(group.lineNumbers.length, `${shown}${more}`)}.`;
  }).join(' ')
);

/** One sentence per reference, in the server's order. Kept for callers that
 *  describe a single reference; the banner and the toast group instead. */
export const describeStaleReference = (reference) => {
  const where = STEP_LABELS[reference?.owning_step] || 'its step';
  if (masterOf(reference) === MASTER_LOCATION) {
    const label = referenceLabel(reference) || 'no location';
    if (reference?.line_number === null || reference?.line_number === undefined) {
      return `Ship-to location ${label} is no longer active - update it on ${where}.`;
    }
    return `Line ${reference?.line_number}: ship-to location ${label} is no longer active - update it on ${where}.`;
  }
  const code = reference?.code || 'no category';
  if (reference?.field === HEADER_CATEGORY_FIELD) {
    return `Spend category ${code} is no longer active - update it on ${where}.`;
  }
  return `Line ${reference?.line_number}: spend category ${code} is no longer active - update it on ${where}.`;
};

/**
 * The retired HEADER code the server reported, or null. Used to choose the
 * retired-incumbent sentence over the no-proposal one at Intake - read off
 * the advisory, never inferred from the category's shape or the master list.
 */
export const retiredHeaderCode = (advisory) => {
  if (!isAdvisoryStale(advisory)) return null;
  const header = advisory.stale_references.find((r) => r && r.field === HEADER_CATEGORY_FIELD);
  return header && header.code ? header.code : null;
};

/** Does the server's list name the header? (routes a regenerate to Intake) */
export const referencesIncludeHeader = (references) => Array.isArray(references)
  && references.some((r) => r && r.field === HEADER_CATEGORY_FIELD);

/** The stamped marker, or null. Read off the STORED assessment only. */
export const readAsIssuedMarker = (riskAssessment) => {
  const marker = riskAssessment && riskAssessment.category_attestation;
  return marker && marker.decision === 'as_issued' ? marker : null;
};

/* ===========================================================================
 * THE ADVISORY BY STEP (Ashok ruling, 2026-09-17).
 *
 * ONE ADVISORY, GROUPED BY THE STEP WHERE EACH THING IS FIXED. A buyer goes to
 * a step and fixes everything there; PROC-000369 found its problems one at a
 * time, at whichever step enforced next. So the banner lists steps, and under
 * each step what is fixed there - stale master references (grouped by master
 * and code, never one row per reference) and past-date hard stops (grouped by
 * code).
 *
 * THE DATE STOPS ARE THE SERVER'S. `advisory.date_stops` comes from
 * routes/_date_stop_advisory.evaluate_date_stops, which mirrors generate_rfp's
 * choice of dates. Nothing here evaluates a date: a JS evaluation would be a
 * second copy of that choice, and the client cannot compute the refinement
 * staleness it turns on.
 * ======================================================================== */

// Wizard order: the order a buyer walks the steps in.
export const ADVISORY_STEP_ORDER = ['intake', 'answers', 'items'];
export const ADVISORY_STEP_NAMES = { intake: 'Intake step', answers: 'Answers step', items: 'Lines step' };

export const advisoryDateStops = (advisory) => (
  advisory && Array.isArray(advisory.date_stops) ? advisory.date_stops.filter((s) => s && typeof s === 'object') : []
);

/** Whether the banner has anything to say: a stale master reference, or a
 *  past date. The master half keeps its own predicate (isAdvisoryStale), which
 *  the Intake gate and the fork read and which a date must not trip. */
export const advisoryHasItems = (advisory) => isAdvisoryStale(advisory) || advisoryDateStops(advisory).length > 0;

const isHeaderReference = (r) => r.line_number === null || r.line_number === undefined;

// A reference's owning step as the server sent it; a payload without one can
// only be one of the four guard fields, owned as the registry owns them.
const ownerOf = (reference) => {
  if (reference.owning_step) return reference.owning_step;
  if (!isHeaderReference(reference)) return 'items';
  return masterOf(reference) === MASTER_LOCATION ? 'answers' : 'intake';
};

const groupDateStopsByCode = (stops) => {
  const groups = [];
  const byCode = new Map();
  stops.forEach((stop) => {
    let group = byCode.get(stop.code);
    if (!group) {
      group = { code: stop.code, field: stop.field, header: false, lineNumbers: [] };
      byCode.set(stop.code, group);
      groups.push(group);
    }
    if (isHeaderReference(stop)) group.header = true;
    else group.lineNumbers.push(stop.line_number);
  });
  return groups;
};

/**
 * [{ step, masterGroups, dateGroups }] in wizard order, steps with nothing to
 * fix omitted. masterGroups are groupStaleReferencesByCode's groups over that
 * step's references; dateGroups are { code, field, header, lineNumbers }.
 */
export const groupAdvisoryByStep = (advisory) => {
  const references = isAdvisoryStale(advisory) ? advisory.stale_references.filter((r) => r && typeof r === 'object') : [];
  const stops = advisoryDateStops(advisory);
  const steps = [...ADVISORY_STEP_ORDER];
  [...references.map(ownerOf), ...stops.map((s) => s.owning_step)].forEach((step) => {
    if (step && !steps.includes(step)) steps.push(step);
  });
  return steps
    .map((step) => ({
      step,
      masterGroups: groupStaleReferencesByCode(references.filter((r) => ownerOf(r) === step)),
      dateGroups: groupDateStopsByCode(stops.filter((s) => s.owning_step === step)),
    }))
    .filter((block) => block.masterGroups.length > 0 || block.dateGroups.length > 0);
};

/**
 * A master group's sentence INSIDE its step block. The step is the heading, so
 * the sentence does not name it again.
 *   Spend category 601090 is no longer active - reselect it.
 *   Spend category 601090 is no longer active on 50 of 51 lines - update them.
 *   Ship-to location NOI001 - NoidaOne is no longer active - reselect it.
 */
export const describeStepMasterGroup = (group, totalLines = null) => {
  const location = (group.master || MASTER_CATEGORY) === MASTER_LOCATION;
  const noun = location ? 'Ship-to location' : 'Spend category';
  const count = group.lineNumbers.length;
  const on = count ? ` on ${linesPhrase(count, totalLines)}` : '';
  if (!group.code) {
    return `No ${location ? 'ship-to location' : 'spend category'} is set${on} - select one.`;
  }
  const status = group.reason === 'shape'
    ? `is not a valid ${location ? 'location' : 'category'} code`
    : 'is no longer active';
  const label = location ? referenceLabel(group) : group.code;
  // ONE VERB FOR MASTER VALUES (Ashok ruling, 2026-09-17): "update" covers a
  // picker and a typed value alike. Dates keep "set a future date", which
  // states the constraint and is deliberately not this verb.
  const remedy = `update ${count > 1 ? 'them' : 'it'}`;
  return `${noun} ${label} ${status}${on} - ${remedy}.`;
};

const DATE_NOUNS = { HVPT: 'The submission deadline', HVND: 'The need-by date', HVSD: 'The start date' };

/**
 *   The submission deadline has passed - set a future date.
 *   The need-by date has passed on 2 of 5 lines - set a future date.
 */
export const describeDateGroup = (group, totalLines = null) => {
  const noun = DATE_NOUNS[group.code] || 'A date';
  const count = group.lineNumbers.length;
  const on = count ? ` on ${linesPhrase(count, totalLines)}` : '';
  return `${noun} has passed${on} - set a future date.`;
};

/**
 * The banner's title. Unchanged (describeAdvisoryTitle) when only master
 * references are stale; a date, or a mix, is named for what it is.
 */
// The title and lead count DISTINCT problems: one code on the header and on
// lines is two entries (two steps) but one retired code.
const distinctProblems = (blocks) => {
  const masterGroups = [];
  const seenMaster = new Set();
  blocks.flatMap((b) => b.masterGroups).forEach((g) => {
    const key = `${g.master}\u0000${g.code}`;
    if (!seenMaster.has(key)) { seenMaster.add(key); masterGroups.push(g); }
  });
  const dateCodes = new Set(blocks.flatMap((b) => b.dateGroups.map((g) => g.code)));
  return { masterGroups, dateItems: dateCodes.size };
};

export const describeStepAdvisoryTitle = (blocks) => {
  const { masterGroups, dateItems } = distinctProblems(blocks);
  if (!dateItems) return describeAdvisoryTitle(masterGroups);
  if (!masterGroups.length) {
    return dateItems === 1 ? 'A date on this requirement has passed' : 'Some dates on this requirement have passed';
  }
  return 'Some master values on this requirement are no longer active and some dates have passed';
};

export const describeStepAdvisoryLead = (blocks) => {
  const { masterGroups, dateItems } = distinctProblems(blocks);
  const masterItems = masterGroups.length;
  const one = masterItems + dateItems === 1;
  // THE RETIREMENT SENTENCE COMES FIRST WHENEVER A MASTER VALUE IS STALE
  // (Ashok ruling, 2026-09-17). It is the one thing that says WHY: the value
  // was valid when the buyer confirmed it and the master changed underneath.
  // Without it a buyer concludes they chose wrongly - the distinction 15ce599
  // drew for the retired incumbent and "is no longer active" drew for lines.
  // NEVER on a dates-only banner: nothing was retired, a date passed.
  let retired = '';
  if (masterItems === 1) retired = 'It was retired from the procurement master after it was confirmed here. ';
  else if (masterItems > 1) retired = 'They were retired from the procurement master after they were confirmed here. ';
  // "can be fixed", not "is fixed": the old wording implied the problem was
  // already being handled.
  const where = one
    ? "It is listed under the step where it can be fixed. You can keep reviewing if you don't want to change anything now - the next step you continue to will ask you to update it."
    : "Each is listed under the step where it can be fixed. You can keep reviewing if you don't want to change anything now - the next step you continue to will ask you to update them.";
  return `${retired}${where}`;
};
